// lint-block-style-separation.ts -- deterministic DESIGN AUTHORITY lint over Cogo UI.
//
// Cogo UI owns structure, semantics, data, and ActionFlow binding. The design
// system owns visual expression. This linter catches generated blocks that blur
// that boundary by embedding raw Tailwind visual utilities, author style objects,
// or visually meaningful nodes with no design-system handle/role.

import { countRawStyleLeaks } from "../../cogo-kg-browser/src/exemplar-quality-core.js"

export type StyleSeparationSeverity = "error" | "warn" | "info"

export type StyleSeparationStatus = "pass" | "warn" | "blocked"

export type StyleSeparationFindingRule =
  | "raw-style-value"
  | "visual-classname-token"
  | "inline-style-object"
  | "inline-visual-style"
  | "malformed-design-handle"
  | "unresolved-design-authority"

export interface StyleSeparationFinding {
  rule: StyleSeparationFindingRule
  path: string
  severity: StyleSeparationSeverity
  message: string
  source?: "className" | "classNameVariants" | "designClass" | "style" | "designAuthority"
  token?: string
  key?: string
  value?: string
}

export interface StyleSeparationSummary {
  nodeCount: number
  designAuthorityCount: number
  rawStyleValueCount: number
  visualClassNameTokenCount: number
  inlineStyleObjectCount: number
  inlineVisualStyleCount: number
  unresolvedDesignAuthorityCount: number
}

export interface StyleSeparationReport {
  schemaVersion: "cogo-style-separation-report.v1"
  status: StyleSeparationStatus
  findings: StyleSeparationFinding[]
  summary: StyleSeparationSummary
}

export interface LintBlockStyleSeparationOptions {
  /**
   * When true, semantic Tailwind visual utilities such as `bg-card` and
   * `rounded-md` are errors. In non-strict mode they are warnings so legacy
   * blocks can be audited before fail-closing generation/publish gates.
   */
  strict?: boolean
  /**
   * When true, every typed UI node must declare a design authority: an @ds
   * handle, structured `design`, role field, token reference, or design-system
   * data attribute. Use this for promotion/readiness gates.
   */
  requireDesignAuthority?: boolean
}

export interface StyleSeparationGeneratorContract {
  schemaVersion: "cogo-style-separation-generator-contract.v1"
  required: true
  validationMode: "strict-fail-closed"
  requireDesignAuthority: true
  designAuthorityFields: string[]
  designSystemInjection: {
    required: true
    owner: "design-system"
    uiOwns: "structure-semantics-data-actionflow"
  }
  classNamePolicy: {
    allowedTokenKinds: string[]
    forbiddenVisualPrefixes: string[]
    maxVisualClassNameTokens: 0
  }
  inlineStylePolicy: {
    allowed: false
    maxInlineStyleObjects: 0
    maxInlineVisualStyleKeys: 0
  }
  rawValuePolicy: {
    forbiddenKinds: string[]
    maxRawStyleValues: 0
  }
  blockAdmission: {
    maxUnresolvedDesignAuthority: 0
  }
  validator: {
    package: "@cogo/headless-core/lint-block-style-separation"
    function: "assessBlockStyleSeparation"
    options: {
      strict: true
      requireDesignAuthority: true
    }
  }
}

type Node = Record<string, unknown>

const DS_HANDLE = /^@ds:[A-Za-z0-9._:-]+$/
const TOKEN_REF = /^(?:ctok|uiox):[A-Za-z0-9:_-]+$/

const RAW_STYLE_VALUE =
  /#[0-9a-fA-F]{3,8}\b|\brgba?\(|\bhsla?\(|\boklch\(|\blab\(|\blch\(|\bvar\(--|\b(?:linear|radial|conic)-gradient\(/

const VISUAL_STYLE_KEYS = new Set([
  "accentColor",
  "background",
  "backgroundColor",
  "backdropFilter",
  "border",
  "borderBlock",
  "borderBlockColor",
  "borderBottom",
  "borderBottomColor",
  "borderColor",
  "borderInline",
  "borderInlineColor",
  "borderLeft",
  "borderLeftColor",
  "borderRadius",
  "borderRight",
  "borderRightColor",
  "borderTop",
  "borderTopColor",
  "boxShadow",
  "caretColor",
  "color",
  "fill",
  "filter",
  "font",
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "letterSpacing",
  "lineHeight",
  "opacity",
  "outline",
  "outlineColor",
  "stroke",
  "textDecoration",
  "textDecorationColor",
  "textShadow",
])

const TEXT_LAYOUT_TOKENS = new Set([
  "text-left",
  "text-center",
  "text-right",
  "text-justify",
  "text-start",
  "text-end",
  "text-balance",
  "text-pretty",
  "text-wrap",
  "text-nowrap",
  "text-ellipsis",
  "text-clip",
])

const VISUAL_CLASS_PREFIXES = [
  "accent",
  "bg",
  "border",
  "caret",
  "decoration",
  "divide",
  "drop-shadow",
  "fill",
  "font",
  "from",
  "gradient",
  "leading",
  "opacity",
  "outline",
  "placeholder",
  "ring",
  "rounded",
  "shadow",
  "stroke",
  "text",
  "to",
  "tracking",
  "underline",
  "via",
]

export const STYLE_SEPARATION_GENERATOR_CONTRACT: StyleSeparationGeneratorContract = {
  schemaVersion: "cogo-style-separation-generator-contract.v1",
  required: true,
  validationMode: "strict-fail-closed",
  requireDesignAuthority: true,
  designAuthorityFields: [
    "@ds:* className/designClass handle",
    "design",
    "designRole",
    "designSystemRole",
    "surfaceRole",
    "styleRole",
    "styleScope",
    "styleRef",
    "tokens",
    "designTokens",
    "dataAttributes.cogoDesignSystemRole",
  ],
  designSystemInjection: {
    required: true,
    owner: "design-system",
    uiOwns: "structure-semantics-data-actionflow",
  },
  classNamePolicy: {
    allowedTokenKinds: [
      "@ds:* design-system handles",
      "ctok:/uiox: structured token refs",
      "layout-only utilities",
      "state/accessibility variants",
    ],
    forbiddenVisualPrefixes: [...VISUAL_CLASS_PREFIXES],
    maxVisualClassNameTokens: 0,
  },
  inlineStylePolicy: {
    allowed: false,
    maxInlineStyleObjects: 0,
    maxInlineVisualStyleKeys: 0,
  },
  rawValuePolicy: {
    forbiddenKinds: [
      "hex-color",
      "rgb-rgba",
      "hsl-hsla",
      "oklch-lab-lch",
      "css-var",
      "gradient",
      "arbitrary-visual-tailwind",
    ],
    maxRawStyleValues: 0,
  },
  blockAdmission: {
    maxUnresolvedDesignAuthority: 0,
  },
  validator: {
    package: "@cogo/headless-core/lint-block-style-separation",
    function: "assessBlockStyleSeparation",
    options: {
      strict: true,
      requireDesignAuthority: true,
    },
  },
}

const asObj = (value: unknown): Node | null =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Node : null

const classesOf = (node: Node, key: "className" | "designClass"): string[] => {
  const value = node[key]
  if (typeof value !== "string") return []
  return splitClassTokensPreservingExpressions(value).filter((token) => !token.startsWith("#{"))
}

const classTokenSources = (node: Node): Array<{
  source: "className" | "classNameVariants" | "designClass"
  tokens: string[]
}> => {
  const sources: Array<{
    source: "className" | "classNameVariants" | "designClass"
    tokens: string[]
  }> = [
    { source: "className", tokens: classesOf(node, "className") },
    { source: "designClass", tokens: classesOf(node, "designClass") },
  ]
  const variants = asObj(node.classNameVariants)
  if (variants) {
    for (const value of Object.values(variants)) {
      if (typeof value !== "string") continue
      sources.push({
        source: "classNameVariants",
        tokens: splitClassTokensPreservingExpressions(value).filter((token) => !token.startsWith("#{")),
      })
    }
  }
  return sources
}

function splitClassTokensPreservingExpressions(value: string): string[] {
  const tokens: string[] = []
  let cursor = 0
  while (cursor < value.length) {
    const open = value.indexOf("#{", cursor)
    const literal = open === -1 ? value.slice(cursor) : value.slice(cursor, open)
    tokens.push(...literal.trim().split(/\s+/).filter(Boolean))
    if (open === -1) break
    const close = value.indexOf("}", open)
    tokens.push(close === -1 ? value.slice(open) : value.slice(open, close + 1))
    if (close === -1) break
    cursor = close + 1
  }
  return tokens
}

/**
 * Return deterministic style-separation findings for a raw Cogo UI node, a
 * `{ root }` wrapper, or a full `{ ui: { root } }` block.
 */
export function lintBlockStyleSeparation(
  block: unknown,
  options: LintBlockStyleSeparationOptions = {},
): StyleSeparationFinding[] {
  return assessBlockStyleSeparation(block, options).findings
}

export function assessBlockStyleSeparation(
  block: unknown,
  options: LintBlockStyleSeparationOptions = {},
): StyleSeparationReport {
  const root = rootNodeOf(block)
  const findings: StyleSeparationFinding[] = []
  const summary: StyleSeparationSummary = {
    nodeCount: 0,
    designAuthorityCount: 0,
    rawStyleValueCount: 0,
    visualClassNameTokenCount: 0,
    inlineStyleObjectCount: 0,
    inlineVisualStyleCount: 0,
    unresolvedDesignAuthorityCount: 0,
  }

  if (!root) return reportOf(findings, summary)

  const seg = (node: Node, index: number | null): string =>
    typeof node.id === "string" && node.id ? `#${node.id}` : index === null ? "root" : `[${index}]`

  const visit = (value: unknown, path: string, index: number | null): void => {
    const node = asObj(value)
    if (!node) return
    const here = path ? `${path} > ${seg(node, index)}` : seg(node, index)
    summary.nodeCount += typeof node.type === "string" ? 1 : 0

    let hasDesignAuthorityForNode = hasDesignAuthority(node)
    if (hasDesignAuthorityForNode) summary.designAuthorityCount += 1

    for (const { source, tokens } of classTokenSources(node)) {
      for (const token of tokens) {
        if (token.startsWith("@ds:")) {
          if (source !== "classNameVariants" || hasSelectedClassNameVariant(node)) {
            hasDesignAuthorityForNode = true
          }
          if (!DS_HANDLE.test(token)) {
            findings.push({
              rule: "malformed-design-handle",
              path: here,
              severity: "error",
              source,
              token,
              message: `Design-system handle "${token}" is malformed; use @ds:<token> or @ds:<kind>.<role>.`,
            })
          }
          continue
        }

        if (isRawClassToken(token)) {
          summary.rawStyleValueCount += 1
          findings.push({
            rule: "raw-style-value",
            path: here,
            severity: "error",
            source,
            token,
            value: token,
            message: `Class token "${token}" embeds a raw visual value; use an @ds handle or structured design token.`,
          })
          continue
        }

        if (isVisualClassToken(token)) {
          summary.visualClassNameTokenCount += 1
          findings.push({
            rule: "visual-classname-token",
            path: here,
            severity: options.strict ? "error" : "warn",
            source,
            token,
            value: token,
            message: `Visual class token "${token}" belongs in the injected design system, not the Cogo UI structure.`,
          })
        }
      }
    }

    const style = asObj(node.style)
    if (style) {
      summary.inlineStyleObjectCount += 1
      findings.push({
        rule: "inline-style-object",
        path: here,
        severity: "error",
        source: "style",
        message: "Inline style object makes the UI node carry renderer-specific visual decisions; use layout/design roles or @ds handles.",
      })

      for (const [key, value] of Object.entries(style)) {
        const valueString = stringifyValue(value)
        if (VISUAL_STYLE_KEYS.has(key)) {
          summary.inlineVisualStyleCount += 1
          findings.push({
            rule: "inline-visual-style",
            path: here,
            severity: "error",
            source: "style",
            key,
            value: valueString,
            message: `style.${key} is visual styling; move it to the design-system token map or structured design role.`,
          })
        }
        if (valueString && isRawStyleValue(valueString)) {
          summary.rawStyleValueCount += 1
          findings.push({
            rule: "raw-style-value",
            path: here,
            severity: "error",
            source: "style",
            key,
            value: valueString,
            message: `style.${key} embeds raw renderer value "${valueString}"; use ctok/uiox/@ds authority instead.`,
          })
        }
      }
    }

    if (options.requireDesignAuthority && typeof node.type === "string" && !hasDesignAuthorityForNode) {
      summary.unresolvedDesignAuthorityCount += 1
      findings.push({
        rule: "unresolved-design-authority",
        path: here,
        severity: "error",
        source: "designAuthority",
        message: `Typed UI node "${node.type}" has no design authority handle/role; declare @ds, design, designRole, surfaceRole, tokens, or cogo design data attributes.`,
      })
    }

    const children = node.children
    if (Array.isArray(children)) children.forEach((child, childIndex) => visit(child, here, childIndex))
    const definitions = asObj(node.definitions)
    if (definitions) {
      Object.entries(definitions).forEach(([name, definition]) => visit(definition, `${here} > definitions.${name}`, null))
    }
  }

  visit(root, "", null)
  const wrapper = asObj(block)
  const wrapperDefinitions = asObj(asObj(wrapper?.ui)?.definitions) ?? asObj(wrapper?.definitions)
  if (wrapperDefinitions && wrapperDefinitions !== asObj(root.definitions)) {
    Object.entries(wrapperDefinitions).forEach(([name, definition]) => visit(definition, `definitions.${name}`, null))
  }
  return reportOf(findings, summary)
}

function rootNodeOf(block: unknown): Node | null {
  const root = asObj(block)
  if (!root) return null
  return asObj(asObj(root.ui)?.root) ?? asObj(root.root) ?? root
}

function reportOf(findings: StyleSeparationFinding[], summary: StyleSeparationSummary): StyleSeparationReport {
  const hasError = findings.some((finding) => finding.severity === "error")
  const hasWarning = findings.some((finding) => finding.severity === "warn")
  return {
    schemaVersion: "cogo-style-separation-report.v1",
    status: hasError ? "blocked" : hasWarning ? "warn" : "pass",
    findings,
    summary,
  }
}

function isRawClassToken(token: string): boolean {
  return countRawStyleLeaks(token) > 0 || isRawStyleValue(token)
}

function isRawStyleValue(value: string): boolean {
  return RAW_STYLE_VALUE.test(value)
}

function isVisualClassToken(token: string): boolean {
  const base = stripVariantPrefixes(token)
  if (!base || TEXT_LAYOUT_TOKENS.has(base)) return false
  if (base === "border" || base === "shadow" || base === "rounded" || base === "underline") return true
  return VISUAL_CLASS_PREFIXES.some((prefix) => base === prefix || base.startsWith(`${prefix}-`))
}

function stripVariantPrefixes(token: string): string {
  if (!token.includes(":")) return token
  return token.slice(token.lastIndexOf(":") + 1)
}

function hasDesignAuthority(node: Node): boolean {
  const directFields = [
    "designRole",
    "designSystemRole",
    "surfaceRole",
    "styleRole",
    "styleScope",
    "styleRef",
  ]
  for (const field of directFields) {
    if (typeof node[field] === "string" && String(node[field]).trim()) return true
  }

  if (hasNonEmptyRecord(node.design) || hasNonEmptyRecord(node.tokens) || hasNonEmptyRecord(node.designTokens)) {
    return true
  }

  for (const { source, tokens } of classTokenSources(node)) {
    if (source === "classNameVariants" && !hasSelectedClassNameVariant(node)) continue
    if (tokens.some((token) => token.startsWith("@ds:") || TOKEN_REF.test(token))) return true
  }

  const attrs = asObj(node.dataAttributes)
  if (attrs) {
    return Object.entries(attrs).some(([key, value]) => {
      const normalized = key.replace(/^data-/, "").replace(/-/g, "").toLowerCase()
      return (
        normalized.includes("cogodesign") ||
        normalized.includes("cogosurface") ||
        normalized.includes("designsystem") ||
        normalized.includes("stylerole")
      ) && typeof value === "string" && value.trim().length > 0
    })
  }

  return false
}

function hasSelectedClassNameVariant(node: Node): boolean {
  return typeof node.variantKey === "string" && node.variantKey.trim().length > 0
}

function stringifyValue(value: unknown): string | undefined {
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return undefined
}

function hasNonEmptyRecord(value: unknown): boolean {
  const record = asObj(value)
  return Boolean(record && Object.keys(record).length > 0)
}
